import {
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  startAfter,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { firebaseAuth, firebaseDb } from '../firebase';
import {
  sanitizeTextInput,
  hasMaliciousPattern,
  isValidLicensePlate,
  REVIEW_TITLE_MAX_LENGTH,
  REVIEW_TEXT_MAX_LENGTH,
  PLATE_MAX_LENGTH,
} from '../utils/validation';

const REVIEWS = 'reviews';
const USERS = 'users';
const REVIEW_VOTES = 'reviewVotes';
const REVIEW_FLAGS = 'reviewFlags';

const toDateString = (value) => {
  if (!value) return new Date().toISOString();
  if (typeof value.toDate === 'function') return value.toDate().toISOString();
  if (value instanceof Date) return value.toISOString();
  return new Date(value).toISOString();
};

const normalizeReview = (id, data) => ({
  id,
  user_id: data.userId,
  plate: data.plate,
  state: data.state,
  title: data.title,
  text: data.text,
  media_urls: data.mediaUrls || [],
  moderation_status: data.moderationStatus || 'approved',
  created_at: toDateString(data.createdAt),
  upvotes: data.upvotes || 0,
  downvotes: data.downvotes || 0,
  flag_count: data.flagCount || 0,
});

const normalizePlate = (plate) => String(plate || '').trim().toUpperCase();
const normalizeState = (state) => String(state || '').trim().toUpperCase();
const DEFAULT_OPERATION_TIMEOUT_MS = 30 * 1000;

function withTimeout(promise, message, timeoutMs = DEFAULT_OPERATION_TIMEOUT_MS) {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error(message)), timeoutMs);
    }),
  ]);
}

function ensureDb() {
  if (!firebaseDb) {
    throw new Error('Firestore is not configured.');
  }
}

function requireUid() {
  const uid = firebaseAuth?.currentUser?.uid;
  if (!uid) throw new Error('Please sign in to continue');
  return uid;
}

async function uploadToCloudinary(file, folder, onSingleProgress) {
  const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
  const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

  if (!cloudName || !uploadPreset) {
    throw new Error('Cloudinary is not configured. Set REACT_APP_CLOUDINARY_CLOUD_NAME and REACT_APP_CLOUDINARY_UPLOAD_PRESET.');
  }

  const form = new FormData();
  form.append('file', file);
  form.append('upload_preset', uploadPreset);
  form.append('folder', folder);

  const url = `https://api.cloudinary.com/v1_1/${cloudName}/auto/upload`;

  return await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.timeout = 60 * 1000;

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && typeof onSingleProgress === 'function') {
        onSingleProgress(event.loaded, event.total);
      }
    };

    xhr.onload = () => {
      let json = {};
      try {
        json = JSON.parse(xhr.responseText || '{}');
      } catch (error) {
        json = {};
      }

      if (xhr.status >= 200 && xhr.status < 300 && json.secure_url) {
        resolve(json.secure_url);
      } else {
        reject(new Error(json.error?.message || 'Cloudinary upload failed'));
      }
    };

    xhr.onerror = () => reject(new Error('Network error while uploading media'));
    xhr.onabort = () => reject(new Error('Upload was interrupted. Please try again.'));
    xhr.ontimeout = () => reject(new Error('Upload timed out. Please check your connection and Cloudinary preset, then retry.'));
    xhr.send(form);
  });
}

async function uploadMediaFiles(files, reviewId, onProgress) {
  const uploadList = (files || []).map((item) => item.file || item).filter(Boolean);
  if (uploadList.length === 0) {
    if (typeof onProgress === 'function') onProgress(100);
    return [];
  }

  const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'video/mp4', 'video/quicktime'];
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
  for (const file of uploadList) {
    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      throw new Error(`File type "${file.type}" is not allowed. Use JPEG, PNG, WebP, GIF, or MP4.`);
    }
    if (file.size > MAX_FILE_SIZE) {
      throw new Error(`File "${file.name}" exceeds the 10 MB size limit.`);
    }
    // Reject path traversal attempts in file names
    if (/(\.\.[/\\]|%2e%2e)/i.test(file.name)) {
      throw new Error('Invalid file name');
    }
  }

  const totalBytes = uploadList.reduce((sum, file) => sum + (file.size || 0), 0) || 1;
  const loadedByIndex = new Array(uploadList.length).fill(0);

  const updateProgress = () => {
    const loaded = loadedByIndex.reduce((sum, value) => sum + value, 0);
    if (typeof onProgress === 'function') {
      onProgress(Math.min(100, Math.round((loaded / totalBytes) * 100)));
    }
  };

  const urls = [];
  for (let index = 0; index < uploadList.length; index += 1) {
    const file = uploadList[index];
    const url = await uploadToCloudinary(file, `reviews/${reviewId}/media`, (loaded, fileTotal) => {
      loadedByIndex[index] = Math.min(loaded, fileTotal || loaded);
      updateProgress();
    });
    loadedByIndex[index] = file.size || loadedByIndex[index];
    updateProgress();
    urls.push(url);
  }

  if (typeof onProgress === 'function') onProgress(100);
  return urls;
}

export async function ensureUserProfile(user) {
  ensureDb();
  if (!user?.id) return;

  const ref = doc(firebaseDb, USERS, user.id);
  await setDoc(ref, {
    uid: user.id,
    email: user.email || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastSeenAt: serverTimestamp(),
  }, { merge: true });
}

export async function createReview({ plate, state, title, text, files = [], isAnonymous = false, onProgress }) {
  ensureDb();
  const uid = firebaseAuth?.currentUser?.uid || null;
  const email = firebaseAuth?.currentUser?.email || null;
  const guestMode = !uid;

  const sanitizedTitle = sanitizeTextInput(title, REVIEW_TITLE_MAX_LENGTH);
  const sanitizedText = sanitizeTextInput(text, REVIEW_TEXT_MAX_LENGTH);
  const sanitizedPlate = sanitizeTextInput(plate, PLATE_MAX_LENGTH).toUpperCase().replace(/\s+/g, '');

  if (!sanitizedTitle || !sanitizedText || !sanitizedPlate) {
    throw new Error('All fields are required');
  }
  if (hasMaliciousPattern(sanitizedTitle) || hasMaliciousPattern(sanitizedText)) {
    throw new Error('Input rejected due to unsafe content');
  }
  if (!isValidLicensePlate(sanitizedPlate)) {
    throw new Error('Invalid license plate format');
  }
  if (files && files.length > 5) {
    throw new Error('Maximum 5 media files allowed');
  }

  const reviewBase = {
    userId: uid,
    email: guestMode ? null : email,
    plate: normalizePlate(plate),
    state: normalizeState(state),
    title: sanitizedTitle,
    text: sanitizedText,
    mediaUrls: [],
    isAnonymous: guestMode || Boolean(isAnonymous),
    moderationStatus: 'approved',
    upvotes: 0,
    downvotes: 0,
    flagCount: 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  // Pre-generate the doc ID so we can upload media before writing to Firestore.
  // This avoids a separate updateDoc call (which would fail for guests since
  // the update rule requires isSignedIn()).
  const ref = doc(collection(firebaseDb, REVIEWS));
  const urls = await uploadMediaFiles(files, ref.id, onProgress);

  await withTimeout(
    setDoc(ref, { ...reviewBase, mediaUrls: urls }),
    'Saving review timed out. Please check your connection and retry.'
  );

  const createdAtIso = new Date().toISOString();
  return normalizeReview(ref.id, {
    ...reviewBase,
    mediaUrls: urls,
    createdAt: createdAtIso,
    updatedAt: createdAtIso,
  });
}

async function getPaged(queryBase, page, pageSize) {
  let cursor = null;
  let pageSnapshot = null;

  for (let currentPage = 1; currentPage <= page; currentPage += 1) {
    const constraints = [...queryBase];
    if (cursor) constraints.push(startAfter(cursor));
    constraints.push(limit(pageSize));

    const q = query(collection(firebaseDb, REVIEWS), ...constraints);
    const snap = await getDocs(q);

    pageSnapshot = snap;
    const docs = snap.docs;
    if (!docs.length) break;
    cursor = docs[docs.length - 1];
  }

  return pageSnapshot;
}

export async function searchReviews({ plate, state, page = 1, pageSize = 10, sort = 'recent' }) {
  ensureDb();
  const normalizedPlate = normalizePlate(plate);
  const normalizedState = normalizeState(state);

  const base = [
    where('plate', '==', normalizedPlate),
    where('state', '==', normalizedState),
    where('moderationStatus', '==', 'approved'),
    orderBy('createdAt', sort === 'oldest' ? 'asc' : 'desc'),
  ];

  const snapshot = await getPaged(base, page, pageSize);
  const reviews = (snapshot?.docs || []).map((d) => normalizeReview(d.id, d.data()));

  const countSnap = await getCountFromServer(query(collection(firebaseDb, REVIEWS), ...base.slice(0, 3)));
  const totalReviews = countSnap.data().count;

  return {
    reviews,
    pagination: {
      currentPage: page,
      totalPages: Math.max(1, Math.ceil(totalReviews / pageSize)),
      totalReviews,
      limit: pageSize,
    },
  };
}

export async function browseReviews({ state, page = 1, pageSize = 20 }) {
  ensureDb();

  const base = [where('moderationStatus', '==', 'approved')];
  if (state) {
    base.push(where('state', '==', normalizeState(state)));
  }
  base.push(orderBy('upvotes', 'desc'));
  base.push(orderBy('createdAt', 'desc'));

  const snapshot = await getPaged(base, page, pageSize);
  const reviews = (snapshot?.docs || []).map((d) => normalizeReview(d.id, d.data()));

  const countQuery = state
    ? query(collection(firebaseDb, REVIEWS), where('moderationStatus', '==', 'approved'), where('state', '==', normalizeState(state)))
    : query(collection(firebaseDb, REVIEWS), where('moderationStatus', '==', 'approved'));

  const countSnap = await getCountFromServer(countQuery);
  const totalReviews = countSnap.data().count;

  return {
    reviews,
    pagination: {
      currentPage: page,
      totalPages: Math.max(1, Math.ceil(totalReviews / pageSize)),
      totalReviews,
      limit: pageSize,
    },
  };
}

export async function getPlateStats(plate, state) {
  const result = await searchReviews({ plate, state, page: 1, pageSize: 100, sort: 'recent' });
  const total = result.pagination.totalReviews;
  return {
    total_reviews: total,
    average_rating: 3,
    rating_1: 0,
    rating_2: 0,
    rating_3: total,
    rating_4: 0,
    rating_5: 0,
  };
}

export async function getUserReviews() {
  ensureDb();
  const uid = requireUid();

  const q = query(
    collection(firebaseDb, REVIEWS),
    where('userId', '==', uid)
  );
  const snap = await getDocs(q);

  return snap.docs
    .map((d) => normalizeReview(d.id, d.data()))
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
}

export async function updateReview(reviewId, { title, text, media = [] }) {
  ensureDb();
  const uid = requireUid();
  const ref = doc(firebaseDb, REVIEWS, String(reviewId));
  const snap = await getDoc(ref);

  if (!snap.exists()) throw new Error('Review not found');
  const current = snap.data();
  if (current.userId !== uid) throw new Error('Not authorized');

  const sanitizedTitle = sanitizeTextInput(title, REVIEW_TITLE_MAX_LENGTH);
  const sanitizedText = sanitizeTextInput(text, REVIEW_TEXT_MAX_LENGTH);
  if (!sanitizedTitle || !sanitizedText) throw new Error('Title and text are required');
  if (hasMaliciousPattern(sanitizedTitle) || hasMaliciousPattern(sanitizedText)) {
    throw new Error('Input rejected due to unsafe content');
  }

  const retainedUrls = media
    .filter((item) => item.type === 'existing' && item.url)
    .map((item) => item.url);
  const newFiles = media
    .filter((item) => item.type !== 'existing' && item.file)
    .map((item) => item.file);

  if (retainedUrls.length + newFiles.length > 5) {
    throw new Error('Maximum 5 media files allowed');
  }

  const uploadedUrls = await uploadMediaFiles(newFiles, reviewId);

  await updateDoc(ref, {
    title: sanitizedTitle,
    text: sanitizedText,
    mediaUrls: [...retainedUrls, ...uploadedUrls],
    updatedAt: serverTimestamp(),
  });
}

export async function deleteReview(reviewId) {
  ensureDb();
  const uid = requireUid();
  const ref = doc(firebaseDb, REVIEWS, String(reviewId));
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Review not found');
  if (snap.data().userId !== uid) throw new Error('Not authorized');

  await deleteDoc(ref);
}

export async function voteOnReview(reviewId, type) {
  ensureDb();
  const uid = requireUid();
  const reviewRef = doc(firebaseDb, REVIEWS, String(reviewId));
  const voteRef = doc(firebaseDb, REVIEW_VOTES, `${reviewId}_${uid}`);

  return await runTransaction(firebaseDb, async (tx) => {
    const [reviewSnap, voteSnap] = await Promise.all([tx.get(reviewRef), tx.get(voteRef)]);
    if (!reviewSnap.exists()) throw new Error('Review not found');

    const review = reviewSnap.data();
    let upvotes = review.upvotes || 0;
    let downvotes = review.downvotes || 0;
    let userVote = null;

    if (voteSnap.exists()) {
      const currentVote = voteSnap.data().type;
      if (currentVote === type) {
        tx.delete(voteRef);
        if (type === 'up') upvotes = Math.max(0, upvotes - 1);
        else downvotes = Math.max(0, downvotes - 1);
        userVote = null;
      } else {
        tx.update(voteRef, { type, updatedAt: serverTimestamp() });
        if (type === 'up') {
          upvotes += 1;
          downvotes = Math.max(0, downvotes - 1);
        } else {
          downvotes += 1;
          upvotes = Math.max(0, upvotes - 1);
        }
        userVote = type;
      }
    } else {
      tx.set(voteRef, { reviewId: String(reviewId), userId: uid, type, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
      if (type === 'up') upvotes += 1;
      else downvotes += 1;
      userVote = type;
    }

    tx.update(reviewRef, { upvotes, downvotes, updatedAt: serverTimestamp() });

    return { upvotes, downvotes, user_vote: userVote };
  });
}

export async function getReviewVote(reviewId) {
  ensureDb();
  const uid = requireUid();
  const voteRef = doc(firebaseDb, REVIEW_VOTES, `${reviewId}_${uid}`);
  const snap = await getDoc(voteRef);
  return snap.exists() ? snap.data().type : null;
}

export async function flagReview(reviewId, reason) {
  ensureDb();
  const uid = requireUid();
  const trimmedReason = String(reason || '').trim();
  if (!trimmedReason) throw new Error('Flag reason is required');

  const reviewRef = doc(firebaseDb, REVIEWS, String(reviewId));
  const flagRef = doc(firebaseDb, REVIEW_FLAGS, `${reviewId}_${uid}`);

  return await runTransaction(firebaseDb, async (tx) => {
    const [reviewSnap, flagSnap] = await Promise.all([tx.get(reviewRef), tx.get(flagRef)]);
    if (!reviewSnap.exists()) throw new Error('Review not found');
    if (flagSnap.exists()) {
      throw new Error('You already flagged this review. Flag comments cannot be edited.');
    }

    const review = reviewSnap.data();
    const flagCount = (review.flagCount || 0) + 1;

    tx.set(flagRef, {
      reviewId: String(reviewId),
      userId: uid,
      reason: trimmedReason,
      createdAt: serverTimestamp(),
    });
    tx.update(reviewRef, { flagCount, updatedAt: serverTimestamp() });

    return { flagged: true, flag_count: flagCount };
  });
}

export async function removeCurrentUserAccount() {
  ensureDb();
  const uid = requireUid();

  const reviewsSnap = await getDocs(query(collection(firebaseDb, REVIEWS), where('userId', '==', uid)));
  const batch = writeBatch(firebaseDb);

  reviewsSnap.docs.forEach((reviewDoc) => {
    batch.delete(doc(firebaseDb, REVIEWS, reviewDoc.id));
  });

  batch.delete(doc(firebaseDb, USERS, uid));
  await batch.commit();
}
